import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { CalendarSync, Plus } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';

import { session } from '@/shared/session/session';

import { useScheduleColumns } from '../components/schedule-columns';
import { scheduleFilters } from '../components/schedule-filters';
import { SchedulesNav } from '../components/schedules-nav';
import { useSchedules } from '../hooks/use-schedules';

const ROUTE_ID = '/_authed/_active/org/schedules/' as const;
const ROUTE_PATH = '/org/schedules/' as const;

function SchedulesList() {
  const { t } = useTranslation('schedules');
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { me } = session.useSession();
  const stores = useMemo(() => me?.stores ?? [], [me?.stores]);
  const filters = useMemo(() => scheduleFilters({ t, stores }), [stores, t]);
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = useSchedules(list.params);
  const columns = useScheduleColumns();
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'schedules',
  });

  const narrowed = Boolean(list.q) || Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;
  const newButton = (tour?: string) => (
    <Can permission="products.write">
      <Button asChild data-tour={tour}>
        <Link to="/org/schedules/new">
          <Plus aria-hidden />
          {t('new')}
        </Link>
      </Button>
    </Can>
  );

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description')}
        actions={newButton('schedules.list.new')}
        tour="schedules.list.header"
      />
      <SchedulesNav />
      {isEmptyList ? (
        <EmptyState
          tour="schedules.list.empty"
          tourLink
          icon={CalendarSync}
          title={t('empty.title')}
          description={t('empty.description')}
          action={newButton()}
        />
      ) : (
        <DataTable
          table={table}
          search={{ placeholder: t('search'), label: t('search') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({ to: '/org/schedules/$scheduleId', params: { scheduleId: row.id } })
          }
          getRowLabel={(row) => row.name}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{
            table: 'schedules.list.table',
            search: 'schedules.list.search',
            filters: 'schedules.list.filters',
          }}
        />
      )}
    </PageContent>
  );
}

export function SchedulesPage() {
  return (
    <FeatureErrorBoundary name="schedules.list">
      <SchedulesList />
    </FeatureErrorBoundary>
  );
}
